import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import loadable from '@loadable/component';
import Stats from 'frontend_stats';
import FullstoryUtil from 'fullstory_util';
import { isEmpty, trackEvent, HeapUtil, parseUTC } from 'core/utils';
import Overlay from 'core/components/Overlay';
import Close from 'core/icons/Close';
import ApiRequest from 'core/api/request';
import { parseError } from 'core/api/utils';
import { stripTags } from 'core/utils/stringUtils';
import getAppData, { getListingData } from 'core/utils/getAppData';
import JsonStorage from 'core/utils/JsonStorage';
import ErrorContainer from 'core/components/Error/ErrorContainer';
import { LoginNaked } from 'core/components/Login';
import { Divider } from 'listing/layout';
import Spinner from 'core/components/Spinner';
const WriteReviewForm = loadable(() => import(
	'./WriteReviewForm'
), {
	fallback: <Spinner size="xlg" />
});

import {
	Wrapper,
	InnerWrapper,
	Heading,
	Header,
	Body,
	DismissBtn,
	SuccessText,
	SuccessButton,
	LoginWrapper,
	Flex,
} from './writeReviewStyles';

// mp.ListingActivated.UNCLAIMED
const isUnclaimed = (activated) => activated === 5;

class WriteReview extends Component {
	static bgServicesOn = true;

	state = {
		processing: false,
		success: false,
		errored: false,

		showLogin: false,
		showFromBg: false,

		contactSellerLogin: false,
		isCustomer: this.props.isCustomer,

		formData: {},
	};

	timeoutId = null;
	dirtyLoginState = false;
	storage = new JsonStorage('writeReviewFormData');

	componentDidMount() {
		this.backgroundDataHandler();
	}

	componentWillUnmount() {
		this.timeoutId && clearTimeout(this.timeoutId);
	}

	resetState = () => {
		this.setState({
			processing: false,
			success: false,
			errored: false,
			showLogin: false,
			showFromBg: false,
			contactSellerLogin: false,
			formData: {},
		});
	};

	show = () => this.props.show || this.state.showFromBg;

	getType() {
		const lv = getListingData() || {};
		if (!lv.listing) return 'listing';
		return isUnclaimed(lv.listing.activated) ? 'unclaimed' : 'listing';
	}

	trackEvent = eventName => {
		Stats.trackGeneric('write_review', {
			step_name: eventName,
			selector: this.getType(),
		});
	};

	fireConfirmation = ({ rating }) => {
		this.trackEvent('confirmation');
		FullstoryUtil.sendEvent('writeReviewConfirmation');
	};

	fireEditConfirmation = ({ rating }) => {
		this.trackEvent('edit_confirmation');
		FullstoryUtil.sendEvent('editReviewConfirmation');
	};

	backgroundDataHandler = () => {
		if (!WriteReview.bgServicesOn || !this.props.isCustomer) return;
		// Only want to do this once so, shut it down
		WriteReview.bgServicesOn = false;

		// 👀 look for background data that we should post 🙌
		const formData = this.storage.get();
		if (!formData) return;

		if (formData.listingId === this.props.listingId) {
			const { mostRecentReviewId, mostRecentReviewDate } = this.props;

			if (mostRecentReviewId) {
				// we have the old version of the review in props, so let's trigger a reload to get the new version
				this.dirtyLoginState = true;
				this.trackEvent('bg_edit_submission');
				this.setState({
					confirmEdit: true,
					showLogin: false,
					showFromBg: true,
					lastReviewDate: mostRecentReviewDate,
					lastReviewId: mostRecentReviewId,
					formData: formData,
				});
			} else {
				this.trackEvent('bg_submission');
				this.postReview(formData, true);
			}
		}
	};

	submitHandler = ({ ...formData }) => {
		const { isCustomer, productId, accessToken } = this.props;
		// Attach productId when available
		if (productId) formData["productId"] = productId;
		if (!isCustomer) {
			if(accessToken) {
				formData['token'] = accessToken;
				this.trackEvent('token_based_submission');
				FullstoryUtil.sendEvent('tokenBasedWriteReviewSubmission');
				trackEvent("token_based_submission", {
					statjoy: [{
						step_name: 'writeReview',
						cus1: accessToken,
					}],
				});
			} else {
				this.trackEvent('pre_login_submission');
				FullstoryUtil.sendEvent('preloginWriteReviewSubmission');
				trackEvent("pre_login_submission", {
					statjoy: [{
						step_name: 'writeReview',
					}],
				});
				return this.setState({ formData, showLogin: true });
			}
		}

		if (this.isEditing()) {
			this.patchReview(formData, this.props.editReviewData.id);
		} else {
			trackEvent("post_review", {
				statjoy: [{
					step_name: 'postReview',
					cus1: accessToken,
				}],
			});
			this.postReview(formData);
		}
	};

	dispatchReviewIfPreviousReviews = (customerId) => {
		const { listingId } = this.props;
		ApiRequest.get('/reviews/', {
			customer_id: customerId,
			listing_id: listingId,
			sort_by: 'created_at__desc',
		})
			.then(res => {
				this.dirtyLoginState = true;

				if (!res.count) {
					// no previous review
					this.postReview(this.state.formData);
				} else {
					this.trackEvent('post_login_edit');
					FullstoryUtil.sendEvent('reviewPostLoginEdit');
					const mostRecentReview = res.results[0];
					this.setState({
						confirmEdit: true,
						showLogin: false,
						lastReviewDate: mostRecentReview.createdAt,
						lastReviewId: mostRecentReview.id,
					});
				}
			})
			.catch(e => {
				this.trackEvent('check_previous_reviews_error');
				FullstoryUtil.sendEvent('checkPreviousReviewsError');
				this.setState({
					errored: parseError(e) || true,
				});
			});
	};

	loginHandler = (customerBlob) => {
		if (this.state.contactSellerLogin) {
			this.setState({ showLogin: false, contactSellerLogin: false });
			this.props.showContactSeller(true, customerBlob.id, customerBlob.email);
		} else {
			this.dispatchReviewIfPreviousReviews(customerBlob.id);
		}
	};

	patchReview = (formData, reviewId) => {
		this.setState({ processing: true });
		this.trackEvent("edit_submitted");

		ApiRequest.patch(`/reviews/${reviewId}/`, formData)
			.then(() => {
				this.storage.remove();
				this.fireEditConfirmation(formData);
				this.setState({
					success: true,
					// ⚡️ Flash success
					processing: false,
					isEditing: true,
					formData: {},
					confirmEdit: false,
				}, this.hideSoon);
			})
			.catch(e => {
				this.trackEvent('edit_error');
				FullstoryUtil.sendEvent('editReviewError');
				this.setState({
					errored: parseError(e) || true,
					processing: false,
				});
			});
	};

	postReview = (formData, showFromBg = false) => {
		this.setState({ processing: true });
		this.trackEvent('submission');

		ApiRequest.post('/reviews/', formData)
			.then((response) => {
				this.storage.remove();
				this.fireConfirmation(formData);
				this.setState({
					success: true,
					// ⚡️ Flash success
					showFromBg: showFromBg,
					processing: false,
					formData: {},
				}, this.hideSoon);
				trackEvent("write_review", {
					braze: [response],
				});
				HeapUtil.track("postedReview");
			})
			.catch(e => {
				this.trackEvent('error');
				FullstoryUtil.sendEvent('writeReviewError');
				this.setState({
					errored: parseError(e) || true,
					processing: false,
				});
			});
	};

	getFormData = () => {
		if (!isEmpty(this.state.formData)) {
			return this.state.formData;
		} else if (!isEmpty(this.props.editReviewData)) {
			return this.props.editReviewData;
		} else {
			return {};
		}
	};

	// this function is needed because we could be editing because a review was passed in as a prop
	// or we found out this is a review after the customer logged in
	isEditing = () => (!isEmpty(this.props.editReviewData) || this.state.isEditing);

	hideSoon = () => {
		if (!this.state.success || !this.show()) return;
		// hide modal after 8 seconds of leaving a review
		this.timeoutId = setTimeout(() => {
			if (this.state.success && this.show()) {
				this.onClose();
			}
		}, 8000);
	};

	onClose = () => {
		const { success, showLogin } = this.state;

		// if login was successful, reload our dirty state
		if (this.dirtyLoginState) {
			window.location.reload(true);
			return;
		}

		if (showLogin && !success) {
			this.trackEvent('pre_login_bail');
		}
		this.setState({
			showLogin: false,
			showFromBg: false,
			success: false,
			processing: false,
		});

		this.props.onClose();
	};

	promptContactSellerLogin = () => {
		this.setState({
			showLogin: true,
			contactSellerLogin: true,
		});
	};

	renderConfirmEdit = () => {
		const { lastReviewDate, formData, lastReviewId } = this.state;
		const niceDate = format(parseUTC(lastReviewDate), "LLL d, yyyy");
		return (
			<div>
				<SuccessText>
					{`We noticed you have a review for this box written on ${niceDate}`}<br/><br/>
					{`Hit submit below to edit that review using the info you just provided.`}<br/><br/>
					{`If you'd like to keep your old review, just refresh.`}
				</SuccessText>
				<SuccessButton onClick={() => this.patchReview(formData, lastReviewId)}>
					Submit
				</SuccessButton>
			</div>
		);
	};

	renderHeader = () => {
		const { isEditing } = this;
		const { confirmEdit, success, showLogin, contactSellerLogin } = this.state;
		return (
			<Heading>
				<Header>
					{success && !isEditing() ?
						'Review Submitted!'
						: success && isEditing() ?
							'Review Updated!'
							: confirmEdit ? "Edit Previous Review?" :
								(showLogin && contactSellerLogin) ?
									"Login to contact the seller." :
									showLogin ?
										'Login to submit your review!' :
										`What did you think?`
					}
				</Header>
				<DismissBtn onClick={this.onClose}>
					<Close width="16" height="16" fill="currentColor"/>
				</DismissBtn>
			</Heading>
		);
	};

	renderSuccess = () => {
		const { isEditing } = this;
		const { listingName } = this.props;
		const renderElements = (text) => (
			<div>
				<SuccessText>
					{text}<br/><br/>
					{'If necessary, your review will be assessed by a moderator before it is live.'}
				</SuccessText>
				<SuccessButton onClick={this.onClose}>
					Continue Shopping
				</SuccessButton>
			</div>);

		const successCopy = (
			isEditing() ?
				`Thanks for updating your review for ${stripTags(listingName)}!`
				: `Thanks for writing a review for ${stripTags(listingName)}!`
		);
		return renderElements(successCopy);
	};

	render() {
		if (!this.show()) return null;
		const { listingId, showContactSeller } = this.props;
		const { processing, success, errored, showLogin, confirmEdit, isCustomer } = this.state;
		const listingType = this.getType();

		return (
			<Overlay noCloseBtn dismiss={this.onClose}>
				<Wrapper>
					<Flex>
						{this.renderHeader()}
						<Divider style={{ margin: 0 }}/>
						<Body>
							{errored ? (
								<ErrorContainer message={errored === true ? undefined : errored}
									onClickButton={this.resetState}
								/>
							) : success ? (
								this.renderSuccess()
							) : showLogin ? (
								<LoginWrapper>
									<LoginNaked
										modalMode
										onBeforeSocialLogin={() => this.storage.save(this.state.formData)}
										onLoginSuccess={this.loginHandler}
										contextUsage="write-a-review-login"
									/>
								</LoginWrapper>
							) : confirmEdit ? (
								this.renderConfirmEdit()
							) : (
								<Fragment>
									<WriteReviewForm
										{...this.getFormData()}
										processing={processing}
										listingId={listingId}
										isEditing={this.isEditing()}
										submitHandler={this.submitHandler}
										showContactSeller={showContactSeller}
										listingType={listingType}
										triggerLogin={this.promptContactSellerLogin}
										isLoggedIn={isCustomer}
									/>
								</Fragment>
							)}
						</Body>
					</Flex>
				</Wrapper>
			</Overlay>
		);
	}

	static propTypes = {
		show: PropTypes.bool,
		isCustomer: PropTypes.bool,
		onClose: PropTypes.func.isRequired,
		listingId: PropTypes.number.isRequired,
		listingName: PropTypes.string,
		showContactSeller: PropTypes.func,
		accessToken: PropTypes.string,

		editReviewData: PropTypes.shape({
			id: PropTypes.number,
			title: PropTypes.string,
			body: PropTypes.string,
			images: PropTypes.array,
			isEditing: PropTypes.bool,
		}),
		// This component may have mounted after a refresh from an SSO login, so let's give the
		// background handler the reviewId so it can patch the right review
		mostRecentReviewId: PropTypes.number,
		mostRecentReviewDate: PropTypes.string,
		productId: PropTypes.number,
	}
}

export default WriteReview;
